import classnames from 'classnames';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { Button } from '@/domains/core/tamagoshiTailwind/components/Button/Button';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useNbRefinedFacets } from '@/domains/productDiscovery/Listings/facets/hooks/useNbRefinedFacets';
import { usePageName } from '@/domains/productDiscovery/Listings/hooks/usePageName';
import { useRefine } from '@/domains/productDiscovery/Listings/hooks/useRefine';
import { useCategoryFamilyId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';
import { clearAllFilters } from '@/productDiscovery/Listings/components/Filters/utils/clearAllFilters';

import { activeFilters, resetFilters } from './translations';

import styles from './QuickFiltersReset.module.scss';

interface Props {
  className?: string;
}

export const QuickFiltersReset: FunctionComponent<Props> = ({ className }) => {
  const rootClassnames = classnames(styles.root, className);
  const nbRefinedFacets = useNbRefinedFacets();
  const pageName = usePageName();
  const refine = useRefine();
  const router = useRouter();

  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();

  const onClearAllFilters = async () => {
    if (pageName) {
      clearAllFilters(pageName, router, refine);

      const { trackResetFilters } = await import(
        '@/domains/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
      );

      Gtm.push(
        trackResetFilters({
          category_id: categoryId,
          category_level: categoryLevel,
          family_id: categoryFamilyId,
        }),
      );
    }
  };

  return (
    <div className={rootClassnames}>
      {nbRefinedFacets > 0 && (
        <div className={styles.reset}>
          <Text type="body2">
            {activeFilters.format({ activeFilters: nbRefinedFacets })}
          </Text>
          <Button
            text={resetFilters}
            variant="link"
            onClick={onClearAllFilters}
            size="M"
          />
        </div>
      )}
    </div>
  );
};

QuickFiltersReset.displayName = 'QuickFiltersReset';
